// api/api.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { IcdPayload } from '.././components/hcc/ActionModal';
import {
  FileData,
  Hcc,
  IcdSourcesAndNotes,
  Ecda,
  EmbedExportCDAAsPDF
} from '../types/Hcc';
import { RootState } from '../app/store';
import { CareGap } from '../types/CareGap';

const serverURL = process.env.REACT_APP_API_ENDPOINT;

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: `${serverURL}/embed`,
    prepareHeaders: (headers, { getState }) => {
      const state = getState() as RootState;
      headers.set('Authorization', `Bearer ${state.userParams.access_token}`);
      headers.set('user-email', state.userParams.user_email);

      return headers;
    }
  }),
  endpoints: (builder) => ({
    getPatientHccs: builder.query<Hcc, string>({
      query: (patientId) => `/patient/${patientId}/hcc`,
      transformResponse: (response: any) => {
        return response.data;
      }
    }),
    getIcdSourcesAndNotes: builder.query<
      IcdSourcesAndNotes,
      { patientId: string | undefined; icdCodeId: string }
    >({
      query: ({ patientId, icdCodeId }) =>
        `/patient/${patientId}/icd/${icdCodeId}`
    }),
    getChangelogCompendiumsByHccId: builder.query({
      query: (hccId) => `changelogCompendium/${hccId}`,
      transformResponse: (response: any) => {
        return response.data;
      }
    }),
    addInternalNote: builder.mutation<object, { hccId: string; notes: string }>(
      {
        query: (data) => ({
          url: `/hcc/${data.hccId}/note`,
          method: 'POST',
          body: {
            notes: data.notes
          }
        }),
        transformResponse: (response: any) => {
          return response.success;
        }
      }
    ),
    setIcdCode: builder.mutation<object, IcdPayload>({
      query: (body) => ({ url: '/hcc', method: 'PUT', body }),
      transformResponse: (response: any) => {
        return response.success;
      }
    }),
    // Missing RETURN type. OBJ by now
    getHccSourceDocument: builder.query<
      FileData,
      { patientId: string; fileHash: string }
    >({
      query: ({ patientId, fileHash }) =>
        `/patient/${patientId}/hcc/source/${fileHash}`
    }),
    deleteIcdNote: builder.mutation<object, { noteId: string }>({
      query: ({ noteId }) => ({
        url: `/patient/icd/delete-note/${noteId}`,
        method: 'POST'
      }),
      transformResponse: (response: any) => {
        return response.success;
      }
    }),
    // Missing RETURN type. OBJ by now
    getUser: builder.query<object, void>({
      query: () => '/user',
      transformResponse: (response: any) => {
        return response.data;
      }
    }),
    // New endpoint to get patient care gaps
    getPatientCareGaps: builder.query<CareGap[], string>({
      query: (patientId) => `/patient/${patientId}/care-gaps`,
      transformResponse: (response: any) => {
        return response.data;
      }
    }),
    getLatestPatientEcda: builder.query<
      Ecda,
      { patientId: string; queryString: string }
    >({
      query: ({ patientId, queryString }) =>
        `/patient/${patientId}/ecda?${queryString}`,
      transformResponse: (response: any) => {
        return response.data;
      }
    }),
    embedExportCDAAsPDF: builder.mutation<
      object,
      {
        queryStr: string;
        payload: object;
      }
    >({
      query: (data) => ({
        url: `export?${data.queryStr}`,
        method: 'POST',
        body: data.payload
      }),
      transformResponse: (response: any) => {
        return response.data;
      }
    }),
    getEmbedExportCDAAsPDF: builder.query<EmbedExportCDAAsPDF, string>({
      query: (requestId) => `export/${requestId}`,
      transformResponse: (response: any) => {
        return response.data;
      }
    }),
    updateCareGaps: builder.mutation<
      object,
      {
        patientId: string;
        careGapId: string;
        status: string | null;
        note: string;
      }
    >({
      query: ({ patientId, careGapId, status, note }) => ({
        url: `patient/${patientId}/care-gaps/update`,
        method: 'POST',
        body: {
          careGapId,
          status,
          note
        }
      }),
      transformResponse: (response: any) => {
        return response.data;
      }
    })
  })
});

// Export hooks for usage in functional components
export const {
  useGetPatientHccsQuery,
  useLazyGetPatientHccsQuery,
  useGetIcdSourcesAndNotesQuery,
  useLazyGetChangelogCompendiumsByHccIdQuery,
  useGetChangelogCompendiumsByHccIdQuery,
  useAddInternalNoteMutation,
  useSetIcdCodeMutation,
  useLazyGetHccSourceDocumentQuery,
  useDeleteIcdNoteMutation,
  useGetUserQuery,
  useLazyGetIcdSourcesAndNotesQuery,
  useGetPatientCareGapsQuery,
  useLazyGetLatestPatientEcdaQuery,
  useUpdateCareGapsMutation,
  useEmbedExportCDAAsPDFMutation,
  useLazyGetEmbedExportCDAAsPDFQuery
} = apiSlice;
